/**
 * «Похожие хозяйства» кабинета (ADR-0014, agroai-6al.43): k ближайших
 * хозяйств по профилю метрик — доля выполненного по стадиям, после срока,
 * задержка отчётов, вода, сдача от плана, карантин, размер — и чем каждое
 * отличается сильнее всего. Расстояние считает сервер (pgvector или та же
 * формула в Python); клиент только показывает.
 *
 * Области хозяйства соседей по району не показывают (фермер видит только
 * своё): сервер отдаёт пустой список с объяснением, и блок не рисуется вовсе.
 */
import { Link } from 'react-router-dom';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { numberLocale } from '../../../i18n/runtime';
import { api } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { roleKind, useAuth } from '../../../auth/context';
import { InfoPopover } from '../../../components/InfoPopover';
import { Card } from '../../ui/Card';
import { SkeletonCard, SkeletonList } from '../../ui/Skeleton';
import { dateShort, num } from '../../lib/format';
import type { FarmCabinetData } from './useFarmCabinet';

const m = defineMessages({
  title: { id: 'farm.similar.title', defaultMessage: 'Похожие хозяйства', description: 'Заголовок карточки на кабинете хозяйства (хоким, менеджер): хозяйства района с близким профилем показателей' },
  skeletonLabel: { id: 'farm.similar.skeletonLabel', defaultMessage: 'похожие хозяйства', description: 'Что грузится (читает диктор как «Загружаю …»): блок похожих хозяйств, винительный падеж, строчными' },
  asOf: { id: 'farm.similar.asOf', defaultMessage: 'по профилю на {date}', description: 'Подпись справа в шапке карточки «Похожие хозяйства»; {date} — дата профиля, уже отформатированная' },
  infoLabel: { id: 'farm.similar.info.label', defaultMessage: 'Как ищутся похожие хозяйства', description: 'aria-label кнопки-подсказки «i» в карточке «Похожие хозяйства»' },
  info: { id: 'farm.similar.info', defaultMessage: 'Профиль хозяйства — числа его метрик на дату среза: выполнено по этапам сезона, после срока, отчёты позже окна, вода (подано от запрошенного), сдано от плана, контуры в карантине, посев. Каждое число нормировано по району (медиана и разброс), «похожие» — ближайшие по всем числам сразу. Ниже у каждого — в чём отличие сильнее всего. Это сходство показателей, а не оценка хозяйства.', description: 'Текст всплывающей подсказки «Как ищутся похожие хозяйства» (хоким, менеджер); объясняет профиль и расстояние' },
  noNeighbors: { id: 'farm.similar.noNeighbors', defaultMessage: 'Похожих хозяйств не нашлось.', description: 'Строка в карточке «Похожие хозяйства», если сервер не вернул соседей и не дал пояснения' },
  anomalous: { id: 'farm.similar.anomalous', defaultMessage: 'Профиль хозяйства далеко от своей группы по размеру: ближайшие соседи всё равно заметно отличаются.', description: 'Пояснение в карточке «Похожие хозяйства»: хозяйство нетипично по размеру' },
  diff: { id: 'farm.similar.diff', defaultMessage: 'сильнее всего отличается: {label} — {other} против {own} здесь', description: 'Подпись под названием соседнего хозяйства; {label} — название показателя (с сервера), {other} — значение у соседа, {own} — значение у этого хозяйства, оба с единицей' },
  almostSame: { id: 'farm.similar.almostSame', defaultMessage: 'профили почти совпадают', description: 'Подпись под названием соседнего хозяйства: заметных отличий нет' },
  missing: { id: 'farm.similar.missing', defaultMessage: 'Нет данных у этого хозяйства: {missing} из {total} признаков (взята медиана района) — сходство по ним не утверждается.', description: 'Сноска под списком похожих хозяйств; {missing}/{total} — числа признаков профиля' },
});

function valueText(v: number | null | undefined, unit: string): string {
  if (v === null || v === undefined) return '—';
  const n = new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 1 }).format(v);
  return unit === '%' ? `${n} %` : `${n} ${unit}`;
}

export function SimilarFarms({ data, farmId }: { data: FarmCabinetData; farmId: number }) {
  const t = useT();
  const similar = useRequest(() => api.similarFarms(farmId, { ...data.ctx.params, k: 5 }).catch(() => null), [farmId, data.ctx.key]);
  const body = similar.data;
  // Скелетон — только тем, кому блок придёт (область района): у хозяйства
  // соседей не видно, и блок не должен мелькнуть и исчезнуть (6al.49).
  const mayShow = roleKind(useAuth().principal) !== 'farm';
  if (similar.state === 'loading' && !similar.data) {
    if (!mayShow) return null;
    return (
      <SkeletonCard label={t(m.skeletonLabel)} title={t(m.title)}>
        <SkeletonList quiet items={3} tone="soft" />
      </SkeletonCard>
    );
  }
  // Область хозяйства: соседей по району не видно — блок не рисуется.
  if (!body || body.context.scope?.kind !== 'district') return null;
  // Профилей на срез нет (сканер не запускался) — справке нечего сказать:
  // пустая карточка на обзоре только перегружает его (замечание владельца 08.10).
  if (!body.run) return null;
  return (
    <Card
        title={t(m.title)}
        aside={
          <span className="cab-similar__aside">
            {body.run ? <span className="cab-list__meta">{t(m.asOf, { date: dateShort(body.run.as_of) })}</span> : null}
            <InfoPopover icon="info" label={t(m.infoLabel)}>
              {t(m.info)}
            </InfoPopover>
          </span>
        }
      >
        {body.neighbors.length === 0 ? (
          <p className="cab-why">{body.notes[0] ?? t(m.noNeighbors)}</p>
        ) : (
          <>
            {body.anomalous ? (
              <p className="cab-why">{t(m.anomalous)}</p>
            ) : null}
            <ul className="cab-list">
              {body.neighbors.map(n => {
                const diff = n.differences[0];
                return (
                  <li className="cab-list__row" key={n.actor_id}>
                    <Link to={`/farms/${n.actor_id}${data.ctx.linkSearch()}`}>{n.name}</Link>
                    <span className="cab-list__meta">
                      {diff ? t(m.diff, { label: diff.label, other: valueText(diff.other, diff.unit), own: valueText(diff.value, diff.unit) }) : t(m.almostSame)}
                    </span>
                  </li>
                );
              })}
            </ul>
            {body.missing.length ? (
              <p className="cab-why">{t(m.missing, { missing: num(body.missing.length), total: num(body.profile.length) })}</p>
            ) : null}
          </>
        )}
    </Card>
  );
}
